<template>
  <div class="min-h-screen bg-white flex flex-col items-center px-6 pt-10 pb-8 md:pt-16" dir="ltr">
    <a :href="homeUrl" class="mb-10 md:mb-14 shrink-0">
      <img src="/img/logo.svg" alt="Better2Know" class="h-10 md:h-12" width="240" height="48" />
    </a>

    <template v-if="props.error.statusCode === 404">
      <div class="w-52 md:w-64 mb-6 shrink-0">
        <!-- Magnifying glass searching a missing document -->
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 200" fill="none" role="img" aria-hidden="true">
          <circle cx="100" cy="95" r="65" fill="#EAF9FA" />
          <rect x="57" y="35" width="82" height="110" rx="4" fill="#CEEDED" opacity=".35" />
          <rect x="52" y="30" width="82" height="110" rx="4" fill="white" stroke="#00A1AA" stroke-width="2" />
          <path d="M110 30v22h24" fill="#E8F9F9" stroke="#00A1AA" stroke-width="2" stroke-linejoin="round" />
          <rect x="66" y="65" width="48" height="4" rx="2" fill="#CEEDED" />
          <rect x="66" y="77" width="36" height="4" rx="2" fill="#CEEDED" />
          <rect x="66" y="89" width="42" height="4" rx="2" fill="#CEEDED" />
          <rect x="66" y="101" width="28" height="4" rx="2" fill="#CEEDED" />
          <rect x="66" y="113" width="34" height="4" rx="2" fill="#CEEDED" />
          <circle cx="158" cy="120" r="34" fill="white" stroke="#00A1AA" stroke-width="3" />
          <line x1="183" y1="145" x2="210" y2="172" stroke="#00A1AA" stroke-width="7" stroke-linecap="round" />
          <text
            x="158"
            y="130"
            text-anchor="middle"
            font-size="34"
            font-weight="700"
            fill="#00A1AA"
            font-family="system-ui,-apple-system,sans-serif"
            opacity=".65"
          >
            ?
          </text>
        </svg>
      </div>

      <p class="text-6xl md:text-7xl font-bold text-primary mb-3 tracking-tight select-none">404</p>

      <h1 class="text-2xl md:text-3xl font-bold text-dark-blue mb-3 text-center">
        {{ t.title }}
      </h1>

      <p class="text-base md:text-lg text-slate-grey max-w-md text-center mb-10 leading-relaxed">
        {{ t.message }}
      </p>
    </template>

    <template v-else>
      <p class="text-6xl md:text-7xl font-bold text-primary mb-4 tracking-tight select-none">
        {{ props.error.statusCode }}
      </p>

      <h1 class="text-2xl md:text-3xl font-bold text-dark-blue mb-3 text-center">
        {{ t.errorTitle }}
      </h1>

      <p class="text-base md:text-lg text-slate-grey max-w-md text-center mb-10 leading-relaxed">
        {{ t.errorMessage }}
      </p>
    </template>

    <a
      :href="homeUrl"
      class="inline-flex items-center justify-center px-8 py-3.5 bg-primary text-white font-semibold rounded-lg hover:opacity-90 transition-opacity text-base md:text-lg min-w-[220px]"
    >
      {{ t.button }}
    </a>
  </div>
</template>

<script setup lang="ts">
import type { RequestError } from "./types/general";

interface ErrorProps {
  error: RequestError;
}

const props = defineProps<ErrorProps>();

// IMPORTANT Log, DO NOT REMOVE IT!
const errorState = useState();
errorState.value = props.error;
console.log(errorState.value);

const VALID_LOCALES = new Set([
  "en",
  "en-ca",
  "en-co",
  "es-co",
  "en-cr",
  "es-cr",
  "en-es",
  "es",
  "es-es",
  "en-ge",
  "en-gb",
  "en-hk",
  "en-hu",
  "hu-hu",
  "en-in",
  "en-ie",
  "en-au",
  "en-pa",
  "es-pa",
  "en-pe",
  "es-pe",
  "en-pl",
  "pl-pl",
  "en-pt",
  "pt-pt",
  "en-ro",
  "en-za",
  "en-ae",
  "en-sa",
  "en-om",
  "en-kw",
  "en-bh",
  "en-qa",
]);

function getBaseLanguage(localeCode: string): string {
  if (localeCode.startsWith("es")) return "es";
  if (localeCode === "hu-hu") return "hu";
  if (localeCode === "pl-pl") return "pl";
  if (localeCode === "pt-pt") return "pt";
  return "en";
}

const errorPath = (() => {
  try {
    return new URL(props.error.url, "http://n").pathname;
  } catch {
    return props.error.url || "";
  }
})();
const errorSegment = errorPath.split("/").filter(Boolean)[0] || "";

const requestUrl = useRequestURL();
const requestSegment = requestUrl.pathname.split("/").filter(Boolean)[0] || "";

const fallbackLocale = useRuntimeConfig().public.siteDefaultLocale?.toString() || "en-au";

const BLOG_LOCALE_MAP: Record<string, string> = {
  en: "", // resolved below from fallbackLocale
  es: "es-es",
};

const resolvedLocale = VALID_LOCALES.has(errorSegment)
  ? errorSegment
  : VALID_LOCALES.has(requestSegment)
    ? requestSegment
    : fallbackLocale;

const locale =
  BLOG_LOCALE_MAP[resolvedLocale] !== undefined ? BLOG_LOCALE_MAP[resolvedLocale] || fallbackLocale : resolvedLocale;
const lang = getBaseLanguage(resolvedLocale);
const homeUrl = `/${locale}/`;

const translations: Record<
  string,
  {
    title: string;
    message: string;
    button: string;
    errorTitle: string;
    errorMessage: string;
  }
> = {
  en: {
    title: "Page not found",
    message: "We're sorry, but the page you're looking for doesn't seem to exist or may have been moved.",
    button: "Go to our homepage",
    errorTitle: "Something went wrong",
    errorMessage: "We're sorry, but something unexpected happened. Please try again.",
  },
  es: {
    title: "Página no encontrada",
    message: "Lo sentimos, pero la página que buscas no parece existir o ha sido movida.",
    button: "Ir a nuestra página de inicio",
    errorTitle: "Algo ha ido mal",
    errorMessage: "Lo sentimos, pero ha ocurrido algo inesperado. Por favor, inténtelo de nuevo.",
  },
  hu: {
    title: "Az oldal nem található",
    message: "Sajnáljuk, de az Ön által keresett oldal nem létezik, vagy áthelyezték.",
    button: "Tovább a honlapunkra",
    errorTitle: "Valami hiba történt",
    errorMessage: "Sajnáljuk, de váratlan hiba történt. Kérjük, próbálja újra.",
  },
  pl: {
    title: "Nie znaleziono strony",
    message: "Przepraszamy, ale szukana strona nie istnieje lub została przeniesiona.",
    button: "Przejdź do strony głównej",
    errorTitle: "Coś poszło nie tak",
    errorMessage: "Przepraszamy, ale wystąpił nieoczekiwany błąd. Spróbuj ponownie.",
  },
  pt: {
    title: "Página não encontrada",
    message: "Lamentamos, mas a página que procura não parece existir ou foi movida.",
    button: "Ir para o início",
    errorTitle: "Algo correu mal",
    errorMessage: "Lamentamos, mas algo inesperado aconteceu. Por favor, tente novamente.",
  },
};

const t = translations[lang] || translations.en;

useHead({
  title: `Better2Know - Error ${props.error.statusCode}`,
  meta: [{ name: "robots", content: "noindex, nofollow" }],
});
</script>
